import { useAppSelector } from '@app/core/hooks/redux-hooks';
import {
  Pwerm2CalculationWaterfallCaseInstrumentTrancheConditionSponsorIrrCorkscrewPeriodDto,
  Pwerm2CalculationWaterfallCaseYearDto,
  Pwerm2CalculationWaterfallCaseYearInstrumentDto,
  Pwerm2CalculationWaterfallCaseYearInstrumentTrancheConditionDto,
  Pwerm2CalculationWaterfallCaseYearInstrumentTrancheDto,
} from '@app/shared/models/contracts/pwerm2-calculation-results-dto';
import { Operator, TargetMetric } from '@app/shared/models/contracts/enums/shared-enums';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { filterPeriodsForYear, hasHistoricPeriods } from '../utils/periodFilters';
import {
  FirstLastInstrument,
  FirstLastTranche,
  OpmWaterfallConditionDto,
} from '@app/shared/models/contracts/opm-calculation-results-dto';
import { l } from '@locale/setupI18n';
import { useShouldUseOpmResults } from './useShouldUseOpmResults';

export type TrancheConditionsCorkscrew = {
  [conditionReference: string]: {
    exitDates: {
      [
        exitDate: string
      ]: Pwerm2CalculationWaterfallCaseInstrumentTrancheConditionSponsorIrrCorkscrewPeriodDto[];
    };
    metric: keyof typeof TargetMetric;
    operator: keyof typeof Operator;
    testValue: number;
    equivalentHurdleValue: number;
  };
};

interface IRRHurdleCalculationsDataReturn {
  groupedByConditionAndExitDate: TrancheConditionsCorkscrew;
}

const processInstrumentTranches = (
  instrument: Pwerm2CalculationWaterfallCaseYearInstrumentDto | FirstLastInstrument,
  exitDate: string,
  isFirstSet: boolean
) => {
  if (!instrument || !instrument.tranches) {
    return [];
  }

  return Object.values(instrument.tranches).flatMap(
    (tranche: Pwerm2CalculationWaterfallCaseYearInstrumentTrancheDto | FirstLastTranche) =>
      (tranche.conditions || []).flatMap(
        (
          condition:
            | Pwerm2CalculationWaterfallCaseYearInstrumentTrancheConditionDto
            | OpmWaterfallConditionDto
        ) => {
          const event = condition.irrHurdleDetails;
          if (!event) {
            return [];
          }

          const processingHistoricPeriods =
            isFirstSet && hasHistoricPeriods(event.periods, exitDate);

          const base = {
            ...event,
            metric: condition.metric,
            operator: condition.operator,
            testValue: condition.testValue,
            equivalentHurdleValue: condition.equivalentHurdleValue,
          };

          if (processingHistoricPeriods) {
            return [
              {
                ...base,
                periods: filterPeriodsForYear(event.periods, exitDate, true),
                exitDate: l('_Historic'),
              },
              {
                ...base,
                periods: filterPeriodsForYear(event.periods, exitDate, false),
                exitDate: exitDate,
              },
            ];
          }

          return [
            {
              ...base,
              periods: filterPeriodsForYear(event.periods, exitDate, false),
              exitDate: exitDate,
            },
          ];
        }
      )
  );
};

const processYearsForInstrument = (
  yearEntries: [string, Pwerm2CalculationWaterfallCaseYearDto][],
  instrumentId: string
) =>
  yearEntries.flatMap(([_, yearObj], idx) => {
    const instrument = yearObj.instruments?.[instrumentId];
    const isFirstSet = idx === 0;
    const exitDate = yearObj.exitDate;
    return processInstrumentTranches(instrument, exitDate, isFirstSet);
  });

export const useIRRHurdleCalculationsData = (
  selectedInstrument: Optional<string>
): IRRHurdleCalculationsDataReturn => {
  const { caseId } = useParams<{ caseId: string }>();
  const useOpmResults = useShouldUseOpmResults();
  const pwermWaterfallResults = useAppSelector(
    (state) => state.pwerm2Calculation.calculatedResults.waterfall
  );
  const opmWaterfallResults = useAppSelector(
    (state) => state.opmCalculation.riskFreeValues.waterfalls
  );

  const pwermInstrumentTrancheHurdles = useMemo(() => {
    if (!caseId || !pwermWaterfallResults?.cases?.[caseId]?.years) {
      return [];
    }

    const yearEntries = Object.entries(pwermWaterfallResults.cases[caseId].years);

    if (selectedInstrument) {
      return processYearsForInstrument(yearEntries, selectedInstrument);
    } else {
      const instrumentIds = Object.keys(pwermWaterfallResults.instruments);
      return instrumentIds.flatMap((instrumentId) =>
        processYearsForInstrument(yearEntries, instrumentId)
      );
    }
  }, [caseId, selectedInstrument, pwermWaterfallResults]);

  const opmInstrumentTrancheHurdles = useMemo(() => {
    if (!opmWaterfallResults) {
      return [];
    }

    return Object.entries(opmWaterfallResults).flatMap(([_, waterfall], waterfallIdx) => {
      const firstWaterfall = Object.values(waterfall.first)[0];
      const instruments = firstWaterfall?.instruments;
      const exitDate = firstWaterfall?.exitDate;

      if (!instruments) {
        return [];
      }

      const instrumentsToProcess = selectedInstrument
        ? { [selectedInstrument]: instruments[selectedInstrument] }
        : instruments;

      return Object.values(instrumentsToProcess).flatMap((instrument) => {
        const isFirstSet = waterfallIdx === 0;
        return processInstrumentTranches(instrument, exitDate, isFirstSet);
      });
    });
  }, [selectedInstrument, opmWaterfallResults]);

  const groupedByConditionAndExitDate = useMemo(
    () =>
      (useOpmResults ? opmInstrumentTrancheHurdles : pwermInstrumentTrancheHurdles).reduce(
        (acc: TrancheConditionsCorkscrew, curr) => {
          const {
            conditionReference,
            exitDate,
            periods,
            metric,
            operator,
            testValue,
            equivalentHurdleValue,
          } = curr;

          let condition = acc[conditionReference];

          if (!condition) {
            condition = acc[conditionReference] = {
              exitDates: {},
              metric,
              operator,
              testValue,
              equivalentHurdleValue,
            };
          }

          if (!condition.exitDates[exitDate]) {
            condition.exitDates[exitDate] = [];

            if (Array.isArray(periods)) {
              condition.exitDates[exitDate].push(...periods);
            } else if (periods) {
              condition.exitDates[exitDate].push(periods);
            }
          }
          return acc;
        },
        {} as TrancheConditionsCorkscrew
      ),
    [pwermInstrumentTrancheHurdles, opmInstrumentTrancheHurdles, useOpmResults]
  );

  return {
    groupedByConditionAndExitDate,
  };
};
